import {
  PaymentRecoveryDetailsQuery,
  PaymentRecoveryListQuery,
  TransactionEventTypeEnum,
} from "@dashboard/graphql";
import { RelayToFlat } from "@dashboard/types";
import { IMoney } from "@dashboard/utils/intl";

export type PaymentRecoveryCheckout = RelayToFlat<
  NonNullable<PaymentRecoveryListQuery["checkouts"]>
>[number];

export type PaymentRecoveryCheckoutDetails = NonNullable<PaymentRecoveryDetailsQuery["checkout"]>;

type ListTransaction = NonNullable<PaymentRecoveryCheckout["transactions"]>[number];
type DetailsTransaction = NonNullable<PaymentRecoveryCheckoutDetails["transactions"]>[number];

type AnyTransaction = ListTransaction | DetailsTransaction;
// The list query only pulls {type, message, createdAt} per event (see
// queries.ts) -- just enough for getLatestEventMessage. Refund-attempt
// tracking needs the fuller shape (amount, id) the details query pulls, so
// it types against DetailsTransaction specifically, not the list's shape.
type DetailsEvent = DetailsTransaction["events"][number];

export interface HeldMoney {
  /** Captured funds — the customer has actually been debited. */
  charged: IMoney;
  /** Reserved funds — a void releases them without a refund. */
  authorized: IMoney;
}

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

/**
 * What the customer is currently out of pocket for, split by whether the money
 * was captured or only reserved. The two need different remedies — a charge is
 * refunded, an authorization is voided — so they are never added together.
 *
 * Nets out refundedAmount/canceledAmount: without this, a transaction that was
 * already refunded (or had a refund requested, successful or not) still counts
 * as fully held, which both overstates what's outstanding and — combined with
 * handleReturnMoney's own charged > 0 check — makes the Refund action look
 * available again for money that already went back (or is already in flight).
 */
export const getHeldMoney = (transactions: AnyTransaction[] | null): HeldMoney => {
  const currency = transactions?.[0]?.chargedAmount.currency ?? "";

  return {
    charged: {
      amount: sum(
        (transactions ?? []).map(
          transaction => transaction.chargedAmount.amount - transaction.refundedAmount.amount,
        ),
      ),
      currency,
    },
    authorized: {
      amount: sum(
        (transactions ?? []).map(
          transaction => transaction.authorizedAmount.amount - transaction.canceledAmount.amount,
        ),
      ),
      currency,
    },
  };
};

export const getItemCount = (lines: Array<{ quantity: number }>) =>
  sum(lines.map(line => line.quantity));

/**
 * Which gateway took the money. `TransactionItem.name` is free text the payment
 * app may never set — none of WSM's do — so the installing App is the reliable
 * identity, with `name` kept as a fallback for apps that do populate it.
 */
export const getGatewayName = (transaction: AnyTransaction): string | null => {
  if (transaction.createdBy?.__typename === "App" && transaction.createdBy.name) {
    return transaction.createdBy.name;
  }

  return transaction.name || null;
};

/** Distinct payment apps involved, in the order they first appear. */
export const getGateways = (transactions: AnyTransaction[] | null) => [
  ...new Set((transactions ?? []).map(getGatewayName).filter((name): name is string => !!name)),
];

/**
 * The most recently touched transaction, by `modifiedAt` -- used for the
 * auto-release countdown below, which cares about transaction activity, not
 * about what to display. For a human-readable "what happened last", see
 * getLatestEventMessage: `TransactionItem.message` (despite its name) is
 * never actually populated by any of WSM's payment apps, so it cannot carry
 * the latest event's message the way this function's callers used to assume.
 */
export const getLatestTransaction = (transactions: AnyTransaction[] | null) =>
  (transactions ?? [])
    .slice()
    .sort((a, b) => new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime())[0];

/** "REFUND_REQUEST" -> "refund request". Used by getLatestEventMessage below,
 * for the list's last-event column -- the details page reuses the real
 * OrderTransaction card instead, which formats its own event types. */
export const formatEventType = (type: TransactionEventTypeEnum | null) =>
  type ? type.split("_").join(" ").toLowerCase() : "-";

/**
 * A human-readable label for whatever happened most recently across every
 * transaction on this checkout, for the list's "Last event" column. Event
 * `message` is frequently blank too (e.g. *_REQUEST events never carry one),
 * so this falls back to the formatted event type.
 */
export const getLatestEventMessage = (transactions: AnyTransaction[] | null): string => {
  const events = (transactions ?? []).flatMap(transaction => transaction.events ?? []);
  const latest = events
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  if (!latest) {
    return "-";
  }

  return latest.message || formatEventType(latest.type);
};

const REFUND_EVENT_TYPES: TransactionEventTypeEnum[] = [
  TransactionEventTypeEnum.REFUND_REQUEST,
  TransactionEventTypeEnum.REFUND_SUCCESS,
  TransactionEventTypeEnum.REFUND_FAILURE,
];

const getLastRefundEvent = (transaction: DetailsTransaction): DetailsEvent | undefined =>
  (transaction.events ?? [])
    .filter(event => REFUND_EVENT_TYPES.includes(event.type as TransactionEventTypeEnum))
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .at(-1);

/**
 * Whether this transaction already has a refund attempt in its history, and
 * how it left off: "pending" (requested, nothing resolved it yet -- firing
 * another REFUND now risks a second concurrent request against the same
 * money), "failed" (the last attempt didn't go through -- a retry may be
 * exactly right, but only once someone has looked at why), or "none".
 *
 * Deliberately does not decide anything on its own -- it exists so callers
 * can surface this to a human before they click Refund again, per the
 * standing rule that a stuck/ambiguous charge gets a human's attention, not
 * an automatic re-fire.
 */
export const getRefundAttemptStatus = (
  transaction: DetailsTransaction,
): "none" | "pending" | "failed" => {
  const last = getLastRefundEvent(transaction);

  if (!last) {
    return "none";
  }

  if (last.type === TransactionEventTypeEnum.REFUND_REQUEST) {
    return "pending";
  }

  if (last.type === TransactionEventTypeEnum.REFUND_FAILURE) {
    return "failed";
  }

  return "none";
};

export interface UnresolvedRefundAttempt {
  transaction: DetailsTransaction;
  status: "pending" | "failed";
  lastEvent: DetailsEvent;
}

/** Every transaction on this checkout whose last refund attempt didn't
 * cleanly resolve, for the confirmation dialog to warn about before another
 * refund request goes out. */
export const getUnresolvedRefundAttempts = (
  transactions: DetailsTransaction[] | null,
): UnresolvedRefundAttempt[] =>
  (transactions ?? []).flatMap(transaction => {
    const status = getRefundAttemptStatus(transaction);

    if (status === "none") {
      return [];
    }

    const lastEvent = getLastRefundEvent(transaction);

    return lastEvent ? [{ transaction, status, lastEvent }] : [];
  });

/**
 * When Saleor's `release-funds-for-abandoned-checkouts` beat task becomes
 * eligible to cancel or refund this checkout's transactions: the last
 * transaction activity plus the channel's TTL. Returns null when the channel
 * has the behaviour switched off, or when the settings could not be read
 * (staff without MANAGE_CHANNELS / HANDLE_PAYMENTS).
 */
export const getAutoReleaseDate = (
  checkout: Pick<PaymentRecoveryCheckoutDetails, "channel" | "transactions">,
): Date | null => {
  const settings = checkout.channel.paymentSettings;

  if (!settings?.releaseFundsForExpiredCheckouts) {
    return null;
  }

  const ttlHours = Number(settings.checkoutTtlBeforeReleasingFunds);

  if (!Number.isFinite(ttlHours)) {
    return null;
  }

  const latest = getLatestTransaction(checkout.transactions);

  if (!latest) {
    return null;
  }

  return new Date(new Date(latest.modifiedAt).getTime() + ttlHours * 60 * 60 * 1000);
};

const RECONCILIATION_STATUS_KEY = "payment_reconciliation_status";
const RECONCILIATION_NOTE_KEY = "payment_reconciliation_note";
const RECONCILIATION_GATEWAY_KEY = "payment_reconciliation_gateway";
const RECONCILIATION_UPDATED_AT_KEY = "payment_reconciliation_updated_at";
const RECONCILIATION_DIFF_KEY = "payment_reconciliation_content_diff";

type MetadataItem = { key: string; value: string };

export interface ReconciliationContentDiff {
  original: Record<string, unknown>;
  at_capture: Record<string, unknown>;
}

export interface ReconciliationInfo {
  status: string;
  note: string;
  gateway: string;
  updatedAt: string | null;
  diff: ReconciliationContentDiff | null;
}

/**
 * Parses the PaymentReconciliation status wsm-app-platform's payment apps
 * write onto checkout metadata (saleor_app_framework/checkout_metadata.py)
 * -- the only way this dashboard can see that row at all, since it lives in
 * a separate database Saleor's GraphQL API has no knowledge of. Same
 * mechanism the `kount` app already uses for kount_decision/omniscore (see
 * PaymentRecoveryDetailsPage). Returns null when nothing was ever written --
 * the common case, since this is only synced at points a human might need
 * to look (a mismatch, an escalation, an abandonment), not on every routine
 * successful charge.
 */
export const getReconciliationInfo = (
  metadata: readonly MetadataItem[] | null | undefined,
): ReconciliationInfo | null => {
  const find = (key: string) => metadata?.find(item => item.key === key)?.value;
  const status = find(RECONCILIATION_STATUS_KEY);

  if (!status) {
    return null;
  }

  const diffRaw = find(RECONCILIATION_DIFF_KEY);
  let diff: ReconciliationInfo["diff"] = null;

  if (diffRaw) {
    try {
      diff = JSON.parse(diffRaw);
    } catch {
      diff = null;
    }
  }

  return {
    status,
    note: find(RECONCILIATION_NOTE_KEY) ?? "",
    gateway: find(RECONCILIATION_GATEWAY_KEY) ?? "",
    updatedAt: find(RECONCILIATION_UPDATED_AT_KEY) ?? null,
    diff,
  };
};

const CONTENT_DIFF_FIELD_LABELS: Record<string, string> = {
  total_price_gross: "Order total",
  shipping_price_gross: "Shipping price",
  shipping_method_id: "Shipping method",
  shipping_address: "Shipping address",
  lines: "Line items",
};

export interface ContentDiffRow {
  field: string;
  before: string;
  after: string;
}

/**
 * Recursively sorts object keys (array element order is left alone --
 * content_snapshot.py's own `lines` array is already sorted by variant_id
 * server-side, so its order is meaningful). Needed because Postgres's
 * `jsonb` column type does not preserve object key insertion order: the
 * "original" and "at_capture" halves of a stored diff can end up with
 * different key orders for the exact same values purely as a storage
 * artifact (original round-tripped through jsonb once already when the row
 * was first created; at_capture is a fresh dict from the same request that
 * detected the mismatch, never round-tripped). Comparing/display without
 * this canonicalization step flags identical values as "changed".
 */
const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }

  if (value !== null && typeof value === "object") {
    const sorted: Record<string, unknown> = {};

    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      sorted[key] = canonicalize((value as Record<string, unknown>)[key]);
    }

    return sorted;
  }

  return value;
};

// Known shipping-app identifier prefixes, for a nicer display label than
// the raw slug -- falls back to Title Case for anything not listed.
const CARRIER_LABELS: Record<string, string> = {
  usps: "USPS",
  fedex: "FedEx",
  ups: "UPS",
};

const titleCase = (segment: string) =>
  segment
    .split(/[_-]/)
    .filter(Boolean)
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join(" ");

/**
 * External (app-provided) shipping method IDs -- the kind a multi-carrier
 * shipping app returns via SHIPPING_LIST_METHODS_FOR_CHECKOUT -- are never
 * a real ShippingMethod database row, so there is no live query that can
 * resolve an official name for one after the fact; the app that quoted it
 * only returns a label at quote time, and Saleor doesn't persist it.
 *
 * The ID itself is `base64("app:{app_identifier}:...")` though (each
 * shipping app builds this ID itself), and the app identifiers this
 * platform uses embed a readable, slugified service name -- e.g.
 * `app:usps.shipping:usps:usps_ground_advantage`. Decoding and
 * reformatting that is a best-effort *inferred* label, not an authoritative
 * one, but far more useful than a raw hash. Anything that doesn't decode
 * into this shape (including a real internal ShippingMethod ID, which is a
 * different base64 payload entirely) is left as the raw value, unchanged.
 */
const formatShippingMethodId = (value: string): string => {
  let decoded: string;

  try {
    decoded = atob(value);
  } catch {
    return value;
  }

  const parts = decoded.split(":");

  if (parts.length < 3 || parts[0] !== "app") {
    return value;
  }

  const carrierSlug = parts[2]?.toLowerCase();
  const carrier = CARRIER_LABELS[carrierSlug] ?? titleCase(carrierSlug ?? "");
  const serviceSlug = parts.slice(3).join(":") || parts[1];
  const service = titleCase(serviceSlug.replace(new RegExp(`^${carrierSlug}_?`), ""));

  return service ? `${carrier} — ${service}` : carrier;
};

const stringifyDiffValue = (field: string, value: unknown): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  if (typeof value === "string") {
    return field === "shipping_method_id" ? formatShippingMethodId(value) : value;
  }

  return JSON.stringify(canonicalize(value));
};

/**
 * A human-readable "what changed" list from a content-mismatch diff (see
 * getReconciliationInfo), field by field, rather than a raw JSON dump --
 * exactly what a mismatch investigation needs, and exactly what the
 * checkout content snapshot (saleor_app_framework/content_snapshot.py)
 * already canonicalizes down to. Only fields that actually differ are
 * returned -- comparison is by canonicalized value, not raw string, so a
 * jsonb key-reordering artifact (see canonicalize's docstring) never shows
 * up as a false-positive change.
 */
export const getContentDiffRows = (diff: ReconciliationContentDiff | null): ContentDiffRow[] => {
  if (!diff) {
    return [];
  }

  const fields = new Set([
    ...Object.keys(diff.original ?? {}),
    ...Object.keys(diff.at_capture ?? {}),
  ]);

  return Array.from(fields)
    .filter(field => {
      const before = canonicalize(diff.original?.[field]);
      const after = canonicalize(diff.at_capture?.[field]);

      return JSON.stringify(before) !== JSON.stringify(after);
    })
    .map(field => ({
      field: CONTENT_DIFF_FIELD_LABELS[field] ?? field,
      before: stringifyDiffValue(field, diff.original?.[field]),
      after: stringifyDiffValue(field, diff.at_capture?.[field]),
    }));
};
